import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { logInvokeProfiles } from './invokeProfilesLogger';

const InvokeHelpDialog: React.FC<{ isLight: boolean; onClose: () => void }> = ({ isLight, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    logInvokeProfiles('app', 'invoke_help_opened');
    return () => { document.body.style.overflow = ''; };
  }, []);

  const panel = isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800';
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const body = isLight ? 'text-neutral-600' : 'text-neutral-300';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const code = isLight ? 'bg-neutral-100 text-neutral-800' : 'bg-neutral-800 text-neutral-200';

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex justify-center items-center p-4">
      <div className={`w-full max-w-lg rounded-xl shadow-xl border ${panel}`}>
        <div className={`flex justify-between items-center px-6 py-4 border-b ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
          <h2 className={`text-lg font-semibold ${title}`}>How invoke profiles work</h2>
          <button type="button" onClick={onClose} className={muted} aria-label="Close help">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className={`p-6 space-y-4 text-sm ${body}`}>
          <p>
            An invoke profile is a tenant rule for how the gateway calls a matched model. Chat models use chat completions. Image models use image generation.
          </p>
          <div>
            <p className={`mb-1 font-medium ${title}`}>Match order</p>
            <p>Exact LLM first, then the first enabled glob. Put specific patterns before broad ones so <code className={`px-1 rounded ${code}`}>gpt-image-*</code> wins over <code className={`px-1 rounded ${code}`}>gpt-*</code>.</p>
          </div>
          <div>
            <p className={`mb-1 font-medium ${title}`}>Samples</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><code className={`px-1 rounded ${code}`}>claude-opus-*</code> → chat completions</li>
              <li><code className={`px-1 rounded ${code}`}>gpt-4o</code> → chat completions</li>
              <li><code className={`px-1 rounded ${code}`}>gpt-image-2.5-flare</code> → image generation</li>
              <li><code className={`px-1 rounded ${code}`}>llama-3</code> → chat completions</li>
            </ul>
          </div>
          <div>
            <p className={`mb-1 font-medium ${title}`}>Add a new profile</p>
            <p>Use Add Invoke Profile. Set a name, pick Chat or Image generation, add one match pattern per line, optionally pin an LLM, then Save.</p>
          </div>
        </div>
        <div className={`flex justify-end px-6 py-4 border-t ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
          <button type="button" onClick={onClose} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm">
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InvokeHelpDialog;
